<template>
    <b-container>

  <b-row class="align-items-center mb-3">
    <b-col>
      <b-nav tabs>
        <b-nav-item
          href="/tasks?view=assigned"
          :active="!$route.query.view || $route.query.view === 'assigned'"
        >
          My Assigned Tasks
        </b-nav-item>

        <b-nav-item
          href="/tasks?view=closed"
          :active="$route.query.view === 'closed'"
        >
          My Closed Tasks
        </b-nav-item>

        <b-nav-item
          href="/tasks?view=open"
          :active="$route.query.view === 'open'"
        >
          All Open Tasks
        </b-nav-item>

        <b-nav-item
          href="/tasks?view=allclosed"
          :active="$route.query.view === 'allclosed'"
        >
          All Closed Tasks
        </b-nav-item>

        <b-nav-item
          href="/tasks/distribution"
          :active="$route.path === '/tasks/distribution'"
        >
          Task Distribution
        </b-nav-item>
      </b-nav>
    </b-col>

    <b-col cols="auto" class="text-right">
      <b-button href="/task/new" variant="secondary">Add Task</b-button>
    </b-col>
  </b-row>
        <b-row class="mb-2">
            <b-col>
                <b-form-group >
                    <b-form-select v-model="statusFilter" :options="statusFilterOptions" />
                </b-form-group>
            </b-col>
            <b-col>
                <b-form-group>
                    <b-form-select v-model="importanceFilter" :options="importanceFilterOptions" />
                </b-form-group>
            </b-col>
            <b-col v-if="is_admin == 1">
                <b-form-group>
                    <b-form-select v-model="userFilter" :options="userFilterOptions" @change="loadTasks" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group>
                    <b-input-group>
                        <b-form-input v-model="filter" type="search" placeholder="Type to Search" />
                        <b-input-group-append>
                            <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                        </b-input-group-append>
                    </b-input-group>
                </b-form-group>
            </b-col>
        </b-row>

        <!-- Loading Spinner -->
        <div v-if="isLoading" class="text-center my-5">
            <b-spinner label="Loading..." variant="primary" type="grow" />
            <div>Loading tasks…</div>
        </div>

        <!-- Table and Pagination -->
        <div v-else>
            <b-table
                striped
                hover
                small
                responsive
                :items="sortedFilteredTasks"
                :fields="fields"
                :per-page="perPage"
                :current-page="currentPage"
            >
                <template #cell(ref_status_id)="data">
                    <b-badge size="sm" :variant="statuses[data.value - 1]?.variant" style="display: block;">
                        {{ statuses[data.value - 1]?.label.toUpperCase() }}
                    </b-badge>
                </template>

                <template #cell(autoid)="data">
                    <span class="d-flex align-items-center">
                        <span :class="importances[data.item.ref_importance_id - 1]?.class" class="mr-2">
                            <font-awesome-icon :icon="importances[data.item.ref_importance_id - 1]?.icon"
                                :title="importances[data.item.ref_importance_id - 1]?.label" />
                        </span>
                        {{ data.item.autoid }}
                    </span>
                </template>

                <template #cell(subject)="data">
                    <b-link :to="`/task/${data.item.task_id}`">
                        {{ data.item.subject }}
                    </b-link>
                </template>

                <template #cell(edit)="data">
                    <b-button size="sm" variant="success" :to="`/task/${data.item.task_id}`">Edit</b-button>
                </template>

                <template #cell(assignee)="data">
                    {{ data.item.assignee }}
                </template>

                <template #cell(date_due)="data">
                    {{ data.value | onlyDate }}
                </template>
            </b-table>

            <hr>
            <b-row>
                <b-col>
                    <b-form-group label="Tasks per page">
                        <b-form-select v-model="perPage" :options="pageOptions" size="sm" />
                    </b-form-group>
                </b-col>
                <b-col cols="10" class="pt-3">
                    <b-pagination
                        v-model="currentPage"
                        :total-rows="sortedFilteredTasks.length"
                        :per-page="perPage"
                        align="end"
                        size="sm"
                        class="mt-3"
                    />
                </b-col>
            </b-row>
        </div>
    </b-container>
</template>

<script>
import TaskDataService from '@/services/TaskDataService';
import UserDataService from '@/services/UserDataService';

export default {
    data() {
        return {
            tasks: [],
            isLoading: true,
            perPage: 10,
            currentPage: 1,
            filter: null,
            statusFilter: '',
            importanceFilter: '',
            userFilter: '',
            userFilterOptions: [{ value: '', text: 'My Tasks' }],
            is_admin: localStorage.getItem('admin'),
            pageOptions: [10, 25, 50, { value: 500, text: 'Show a lot' }],
            fields: [
                { key: 'ref_status_id', label: '', sortable: false },
                { key: 'autoid', label: '#', sortable: true },
                { key: 'subject', label: 'Subject', sortable: true },
                { key: 'edit', label: '', sortable: false },
                { key: 'assignee', label: 'Assigned', sortable: true },
                { key: 'date_due', label: 'Due', sortable: true }
            ],
            statuses: [
                { id: 1, label: 'new', variant: 'success' },
                { id: 2, label: 'accepted', variant: 'success' },
                { id: 3, label: 'in progress', variant: 'success' },                
                { id: 4, label: 'waiting', variant: 'danger' },     
                { id: 5, label: 'completed', variant: 'success' },           
                { id: 6, label: 'reopened', variant: 'danger' },
                { id: 7, label: 'declined', variant: 'secondary' },
                { id: 8, label: 'closed', variant: 'secondary' }
            ],
            importances: [
                { id: 1, label: 'Low', icon: 'fas fa-arrow-circle-down', class: 'text-primary' },
                { id: 2, label: 'Normal', icon: 'fas fa-minus-circle', class: 'text-success' },
                { id: 3, label: 'High', icon: 'fas fa-arrow-circle-up', class: 'text-warning' },
                { id: 4, label: 'Critical', icon: 'fas fa-exclamation-circle', class: 'text-danger' }
            ]
        };
    },
    computed: {
        statusFilterOptions() {
            return [{ value: '', text: 'All Statuses' }, ...this.statuses.map(s => ({ value: s.id, text: s.label }))];
        },
        importanceFilterOptions() {
            return [{ value: '', text: 'All Importances' }, ...this.importances.map(i => ({ value: i.id, text: i.label }))];
        },
        sortedFilteredTasks() {
            return this.tasks
                .filter(task => {
                    const statusMatch = [1, 2, 3].includes(task.ref_status_id);
                    const matchesStatus = this.statusFilter ? task.ref_status_id === this.statusFilter : statusMatch;
                    const matchesImportance = this.importanceFilter ? task.ref_importance_id === this.importanceFilter : true;
                    const matchesText = this.filter
                        ? task.subject?.toLowerCase().includes(this.filter.toLowerCase()) ||
                          task.assignee?.toLowerCase().includes(this.filter.toLowerCase())
                        : true;
                    return matchesStatus && matchesImportance && matchesText;
                })
                .sort((b, a) => (a.ref_importance_id || 0) - (b.ref_importance_id || 0));
        }
    },
    methods: {
        loadTasks() {
            this.isLoading = true;

            let status_list = '1,2,3,4,6';
            let assignee = this.userFilter || '';

            if(this.$route.query.view == 'closed') {
                status_list = '5,7,8';
            }

            if(this.$route.query.view == 'open') {
                status_list = '1,2,3,4,6';
                assignee = assignee || 'any';
            }

            if(this.$route.query.view == 'allclosed') {
                status_list = '5,7,8';
                assignee = assignee || 'any';
            }

            if(this.$route.query.user_id !== undefined) {
                assignee = this.$route.query.user_id;
            }

            TaskDataService.getAll(status_list, assignee, 'ref_importance_id,desc', 300)
                .then(res => {
                    if (res && res.data && Array.isArray(res.data)) {
                        this.tasks = res.data;
                    } else {
                        this.tasks = [];
                        console.error('Failed to load tasks:', res?.message || 'Unknown error');
                    }
                    this.isLoading = false;
                })
                .catch(err => {
                    this.$emit('error', err);
                    this.tasks = [];
                    this.isLoading = false;
                });
        }
    },
    created() {
        // Load user options for admin filter
        if (this.is_admin == 1) {
            UserDataService.getActiveUsers().then(res => {
                this.userFilterOptions = [
                    { value: '', text: 'My Tasks' },
                    { value: 'any', text: 'All Users' },
                    ...res.data.map(u => ({ value: u.value, text: u.text }))
                ];
            });
        }

        this.loadTasks();
    }
};
</script>
